import { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';

const getClient = () => {
  try {
    return generateClient<Schema>();
  } catch {
    return null;
  }
};

export interface Bundle {
  id: string;
  name: string;
  description?: string;
  tourSlugs: string[];
  price: number;
  priceChild: number;
  cruise: boolean;
  cayes: boolean;
  isActive: boolean;
  displayOrder: number;
}

interface UseBundlesResult {
  bundles: Bundle[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useBundles(): UseBundlesResult {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBundles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const client = getClient();
      if (!client) {
        setBundles([]);
        setLoading(false);
        return;
      }
      const { data } = await client.models.Bundle.list();

      // Filter for active bundles client-side (avoids TypeScript issues during sandbox deploy)
      const activeBundles = data.filter((bundle) => bundle.isActive !== false);

      // Convert to Bundle interface and sort by displayOrder
      const convertedBundles: Bundle[] = activeBundles.map((bundle) => ({
        id: bundle.id,
        name: bundle.name,
        description: bundle.description || undefined,
        tourSlugs: bundle.tourSlugs || [],
        price: bundle.price,
        priceChild: bundle.priceChild || 0,
        cruise: bundle.cruise || false,
        cayes: bundle.cayes || false,
        isActive: bundle.isActive ?? true,
        displayOrder: bundle.displayOrder || 0,
      }));

      // Sort by displayOrder descending
      convertedBundles.sort((a, b) => b.displayOrder - a.displayOrder);

      setBundles(convertedBundles);
    } catch (err) {
      console.error('Error fetching bundles:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBundles();
  }, [fetchBundles]);

  return { bundles, loading, error, refetch: fetchBundles };
}
